import React, { useRef } from "react";
import aduanaTheme from "../../components/DuelSkin/aduanaTheme";

const { labels } = aduanaTheme;

const TABS = [
  { id: "active", label: labels.activeTabLabel },
  { id: "history", label: labels.historyTabLabel },
];

/**
 * DuelsTabs — presentational tab rail for `/duels` (design.md D2, D5, D8).
 * ARIA tabs pattern: `role="tablist"` + `role="tab"` buttons, roving
 * `tabIndex`, Arrow-key wrap navigation, Home/End jump-to-edge — selection
 * follows focus, per design.md D8. Controlled component: `activeTab` +
 * `onChange` are owned by the caller (`DuelsPage` per design.md D1) — this
 * component holds no state of its own and does not render any tab panel.
 *
 * Visual identity sourced exclusively from `aduanaTheme.js` (labels) and
 * `.duel-skin-tabs`/`.duel-skin-tab(--active)` in `aduanaSkin.css` — spec
 * duel-visual-identity, "New /duels page components comply". No themed
 * literal is hardcoded here.
 *
 * @param {Object} props
 * @param {"active"|"history"} props.activeTab
 * @param {Function} props.onChange - Invoked with the newly selected tab id.
 */
export const DuelsTabs = ({ activeTab, onChange }) => {
  const tabRefs = useRef([]);

  const selectByIndex = (index) => {
    onChange(TABS[index].id);
    tabRefs.current[index]?.focus();
  };

  const handleKeyDown = (event, index) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        selectByIndex((index + 1) % TABS.length);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        selectByIndex((index - 1 + TABS.length) % TABS.length);
        break;
      case "Home":
        event.preventDefault();
        selectByIndex(0);
        break;
      case "End":
        event.preventDefault();
        selectByIndex(TABS.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="duel-skin-tabs" role="tablist">
      {TABS.map((tab, index) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`duel-tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`duel-tabpanel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            className={`duel-skin-tab${isActive ? " duel-skin-tab--active" : ""}`}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default DuelsTabs;
